'use client';

import { useEffect, useState } from 'react';
import { MatchCricket, MatchOther } from '@/features/sportsbook-aside/components/match-item';
import { MatchModelBase, SportMatchModel } from '@nf/types/odds';
import { TopbarMatchList } from './top-bar-match-list';
import { useStreamingStore } from '@/store/streaming';
import { Scoreboard, ScoreboardSportId } from '@/features/sportsbook-aside/components/scoreboard';
import { scoreboardService } from '@/services/scoreboard.service';
import { useAtomValue } from 'jotai';
import { oddsPageAtom } from '@/atoms/odds-page';
import { getHomeTeamName, getLeagueName, getTeamName } from '@/utils/odds-utils';
import { useTranslations } from 'next-intl';
import { BackButton } from '@/components/feature-buttons/back-button';
import { MyBetsButton } from '@/components/feature-buttons/my-bets-button';
import { useFilterSabaString } from '@/hooks/query/use-saba-filter';
import { TopbarMatchListDropdownButton } from './top-bar-match-list-dropdown-button';
import { useAccountStatus } from '@/hooks/query/account';
import { cx } from 'styled-system/css';
import { topBarStyles } from './top-bar.style';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import { KafkaNote } from '@nf/utils-common/constants';
import { useAnalyseAttribute } from '@/features/next-analyse';
import { useBreakpoint } from '@/features/breakpoint/contexts';
import { Breakpoint } from '@/features/breakpoint';

const TopBarStatementButton = ({ clickHandler }: {clickHandler: () => void}) => {
	const taggedElement = useAnalyseAttribute('switch-to-statement-via-single-match');
	return (
		<div
			onClick={clickHandler}
			ref={taggedElement}
		>
			<MyBetsButton from="single-match" />
		</div>
	);
};
interface TopBarProps {
	match: SportMatchModel;
	isAsideLayout: boolean;
}

/**
 * Single Match > Top Bar
 * @description 此元件在 width < 1250 時置中顯示，在 width >= 1250 時顯示於右側 aside 區塊
 * @see SingleMatchHeader
 */
export const TopBar = (topBarProps: TopBarProps) => {
	const { match, isAsideLayout } = topBarProps;
	const t = useTranslations('General');
	const { data: actStatus } = useAccountStatus();
	const oddsData = useAtomValue(oddsPageAtom);
	const { filterSabaString } = useFilterSabaString();
	const leagueName = filterSabaString(getLeagueName(match.LeagueId, oddsData?.leagueNameMap), match.LeagueGroupId);
	const homeTeamName = getHomeTeamName(match.TeamId1, match.Neu, t('Neutral_Team'), oddsData?.teamNameMap);
	const awayTeamName = getTeamName(match.TeamId2, oddsData?.teamNameMap);
	const { hasOtherScoreboard } = scoreboardService.getScoreboardInfo(match);
	const [isListOpen, setIsListOpen] = useState(false);
	const [isShowScoreboard, setIsShowScoreboard] = useState(!isAsideLayout && match?.IsLive ? true : false);
	const [streamingType] = useStreamingStore(
		state => [state.streamingType],
	);
	const [showShowTopBarScoreboardState, setShowTopBarScoreboardState] = useState(false);
	const [showShowTopBarScore, setShowTopBarScore] = useState(false);
	const view = useAppearanceStore(state => state.view);
	const { breakpoint } = useBreakpoint();

	const handleListOpen = () => {
		if (!isAsideLayout) {
			if (!isListOpen) { // when open dropdown
				setKafka();
			}
			setIsListOpen(!isListOpen)
		}
	}

	const setKafka = () => {
		const isGalaxy = view === ViewEnum.galaxy;
		const kafkaData = {
			note: isGalaxy ? KafkaNote.GalaxyV2 : KafkaNote.MarsV1,
			type: isGalaxy ? 9 : 6,
			action: isGalaxy ? 1 : 21
		}
		setKafkaNormal(kafkaData);
	}

	useEffect(() => {
		const handleScroll = () => {
			let scrollContainer: Window | HTMLElement | null;
			const targetElement = document.querySelector<HTMLElement>('.video');

			if (breakpoint < Breakpoint.md) {
				scrollContainer = window;
			} else {
				scrollContainer = document.querySelector<HTMLElement>('.main-wrap__content');
			}

			if (scrollContainer && targetElement) {
				const windowHeight = (scrollContainer as Window).innerHeight || (scrollContainer as HTMLElement).offsetHeight;
				const scrollPosition = (scrollContainer as Window).scrollY || (scrollContainer as HTMLElement).scrollTop;
				const targetPosition = targetElement.offsetTop;
				const halfWindowHeight = windowHeight / 3.5;
				const targetVisiblePosition = targetPosition - halfWindowHeight;
				const targetBottomPosition = targetPosition + targetElement.offsetHeight;

				if (breakpoint > Breakpoint.lg) {
					setShowTopBarScoreboardState(false);
					setShowTopBarScore(false);
				} else if (scrollPosition > targetVisiblePosition && scrollPosition > targetBottomPosition) {
					setShowTopBarScoreboardState(true);
					setShowTopBarScore(true);
				} else {
					setShowTopBarScoreboardState(false);
					setShowTopBarScore(false);
				}
			}
		};

		const scrollContainer = breakpoint < Breakpoint.md ? window : document.querySelector<HTMLElement>('.main-wrap__content');
		if (scrollContainer) {
			scrollContainer.addEventListener('scroll', handleScroll);
		}

		return () => {
			if (scrollContainer) {
				scrollContainer.removeEventListener('scroll', handleScroll);
			}
		};
	}, [breakpoint]);

	return (
		<div
			className={cx('single-topbar', isAsideLayout ? topBarStyles.topBarInAside : topBarStyles.topBar)}
			data-show-match={streamingType || showShowTopBarScore ? true : false}
			data-show-scoreboard={isShowScoreboard}
		>
			<div className="single-topbar__wrap">
				<BackButton isAsideLayout={isAsideLayout} />
				<div className="single-topbar__content">
					<div className="single-topbar__info">
						{
							streamingType || showShowTopBarScore ?
								(
									match?.GameID === 50 ? (
										<>
											<MatchCricket
												match={match}
												home={homeTeamName}
												away={awayTeamName}
											/>
											<TopbarMatchListDropdownButton
												isListOpen={isListOpen}
												handleListOpen={handleListOpen}
												isLive={match.IsLive}
												isAsideLayout={isAsideLayout}
											/>
										</>
									) : (
										<MatchOther
											match={match}
											home={homeTeamName}
											away={awayTeamName}
											isListOpen={isListOpen}
											handleListOpen={handleListOpen}
											isAsideLayout={isAsideLayout}
										/>
									)
								)
								: (
									<div className="single-topbar__league" onClick={handleListOpen}>
										<span>{leagueName}</span>
										<TopbarMatchListDropdownButton
											isListOpen={isListOpen}
											isLive={match.IsLive}
											isAsideLayout={isAsideLayout}
										/>
									</div>
								)
						}
					</div>
					{actStatus === 2 ? 
						<TopBarStatementButton
							clickHandler={() => {
								if (view === ViewEnum.mars) {
									const kafkaData = {
										note: KafkaNote.MarsV1,
										type: 6,
										action: 22
									}
									setKafkaNormal(kafkaData);
								}
							}}
						/>
						: null
					}
				</div>
			</div>
			{
				match.IsLive ? (
					<TopbarMatchList
						matchId={match.MatchId}
						isListOpen={isListOpen}
						setIsListOpen={setIsListOpen}
					/>
				) : null
			}
			{
				isShowScoreboard && hasOtherScoreboard && showShowTopBarScoreboardState && streamingType === '' ? (
					<Scoreboard sportId={match.GameID as ScoreboardSportId} matchModelData={match as MatchModelBase} />
				) : null
			}
		</div>
	);
};